import { Link } from "react-router-dom";
import {
  ChatCircleIcon as ChatCircle,
  FileTextIcon as FileText,
} from "@phosphor-icons/react";

export function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
        active
          ? "border-[var(--ml-primary)] text-[var(--ml-primary)]"
          : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text-primary)]"
      }`}
    >
      {children}
    </button>
  );
}

export function EmptyTab({ icon, title, body, ctaLabel, ctaTo }) {
  return (
    <div className="ml-card p-10 text-center">
      <div className="mx-auto mb-3 w-fit">{icon}</div>
      <div className="font-medium mb-1">{title}</div>
      <p className="text-sm text-[var(--ml-text-secondary)] mb-5 max-w-md mx-auto">
        {body}
      </p>
      <Link to={ctaTo} className="ml-btn-primary">
        {ctaLabel}
      </Link>
    </div>
  );
}

export function SessionsTab({ sessions, agentName }) {
  return (
    <div data-testid="workspace-sessions-tab">
      {sessions.length === 0 ? (
        <EmptyTab
          icon={<ChatCircle size={28} color="#0f62fe" weight="duotone" />}
          title="No sessions linked yet"
          body="Open the Agent Hub, start a session, and assign it to this workspace via the Workspace selector at the top of the chat."
          ctaLabel="Open Agent Hub"
          ctaTo="/hub"
        />
      ) : (
        <div className="ml-card divide-y divide-[var(--ml-border)]">
          {sessions.map((s) => (
            <Link
              to={`/chat/${s.agent_id}?s=${s.id}`}
              key={s.id}
              className="p-4 lg:p-5 flex items-center justify-between gap-4 hover:bg-[var(--ml-surface)]/50"
              data-testid={`ws-session-${s.id}`}
            >
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-9 h-9 bg-[var(--ml-surface)] border border-[var(--ml-border)] flex items-center justify-center shrink-0">
                  <ChatCircle size={16} weight="bold" color="#0f62fe" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.title}</div>
                  <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                    {agentName(s.agent_id)} · {s.message_count} msg ·{" "}
                    {new Date(s.updated_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function DeliverablesTab({ deliverables }) {
  return (
    <div data-testid="workspace-deliverables-tab">
      {deliverables.length === 0 ? (
        <EmptyTab
          icon={<FileText size={28} color="#0f62fe" weight="duotone" />}
          title="No deliverables linked yet"
          body="Generate a deliverable from the Deliverables page and assign this workspace in the form."
          ctaLabel="Go to Deliverables"
          ctaTo="/deliverables"
        />
      ) : (
        <div className="ml-card divide-y divide-[var(--ml-border)]">
          {deliverables.map((d) => (
            <Link
              to={`/deliverables?open=${d.id}`}
              key={d.id}
              className="p-4 lg:p-5 flex items-center justify-between gap-4 hover:bg-[var(--ml-surface)]/50"
              data-testid={`ws-deliv-${d.id}`}
            >
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-9 h-9 bg-[var(--ml-surface)] border border-[var(--ml-border)] flex items-center justify-center shrink-0">
                  <FileText size={16} weight="bold" color="#0f62fe" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {d.title}
                    {d.status === "generating" && (
                      <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[var(--ml-warning)]/20 text-[#8a6d00] border border-[var(--ml-warning)]/40">
                        Generating
                      </span>
                    )}
                  </div>
                  <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                    {d.deliverable_type.replace(/_/g, " ")} ·{" "}
                    {new Date(d.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
